import { loadStripe, type Stripe, type Appearance } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { useMemo, type ReactNode } from 'react';
import { useTheme } from '@/hooks/use-theme';

let stripePromise: Promise<Stripe | null> | null = null;
function getStripe(): Promise<Stripe | null> {
  if (stripePromise === null) {
    const key = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined;
    stripePromise = loadStripe(key ?? '');
  }
  return stripePromise;
}

// The `appearance` object is Stripe SDK config, not a React style prop — values
// are read from the live CSS custom properties on :root so the PCI-scoped iframe
// tracks the Nula theme (light/dark) instead of Stripe's default styling.
function buildAppearance(): Appearance {
  const cs = getComputedStyle(document.documentElement);
  const v = (name: string, fallback: string) => cs.getPropertyValue(name).trim() || fallback;
  return {
    theme: 'flat',
    variables: {
      colorPrimary: v('--primary', '#5EC8E5'),
      colorBackground: v('--surface-container-lowest', '#FFFFFF'),
      colorText: v('--on-surface', '#202020'),
      colorDanger: v('--error', '#9F403D'),
      fontFamily: v('--font-body', 'Inter, system-ui, sans-serif'),
      borderRadius: v('--radius-md', '8px'),
    },
  };
}

export function StripeElementsProvider({
  clientSecret,
  children,
}: {
  clientSecret: string;
  children: ReactNode;
}) {
  const { resolvedTheme } = useTheme();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- resolvedTheme triggers a re-read of the CSS custom properties buildAppearance() reads directly from the DOM; it isn't itself a data dependency
  const appearance = useMemo(() => buildAppearance(), [resolvedTheme]);

  return (
    <Elements stripe={getStripe()} options={{ clientSecret, appearance }}>
      {children}
    </Elements>
  );
}
